import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from "framer-motion";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

// Aceternity Tracing Beam, attached to chapter positions rather than page length.
export function TracingBeam({ children, headingIds }: { children: ReactNode; headingIds: string[] }) {
  const root = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const target = useMotionValue(0);
  const spring = useSpring(target, { stiffness: 500, damping: 90 });
  const beam = reduced ? target : spring;
  const gradient = useId();
  const [stops, setStops] = useState<number[]>([]);
  const [height, setHeight] = useState(0);
  const tail = useTransform(beam, value => Math.max(stops[0] ?? 0, value - 45));
  const key = headingIds.join("|");
  useEffect(() => {
    const container = root.current;
    if (!container) return;
    let frame = 0;
    let initialized = false;
    let positions: number[] = [];
    const place = (position: number) => {
      target.set(position);
      if (!initialized || reduced) { spring.jump(position); initialized = true; }
    };
    const update = () => {
      frame = 0;
      if (!positions.length || container.getClientRects().length === 0) return;
      const threshold = window.innerHeight * .35;
      const headings = headingIds.map(id => document.getElementById(id));
      let index = -1;
      headings.forEach((heading, i) => { if (heading && heading.getBoundingClientRect().top <= threshold) index = i; });
      if (index < 0) { place(positions[0]); return; }
      const current = headings[index]?.getBoundingClientRect().top ?? threshold;
      const next = headings[index + 1]?.getBoundingClientRect().top;
      const fraction = reduced || next === undefined ? 0 : Math.max(0, Math.min(1, (threshold - current) / Math.max(1, next - current)));
      place(positions[index] + ((positions[index + 1] ?? positions[index]) - positions[index]) * fraction);
    };
    const measure = () => {
      const rect = container.getBoundingClientRect();
      positions = Array.from(container.querySelectorAll("nav a")).map(link => {
        const box = link.getBoundingClientRect(); return box.top - rect.top + box.height / 2;
      });
      setStops(positions); setHeight(rect.height); update();
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", measure);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); window.removeEventListener("scroll", schedule); window.removeEventListener("resize", measure); };
  }, [key, reduced, target, spring]);
  return <div ref={root} className="tracing-beam">
    {stops.length > 0 && <svg className="reading-trace" width="16" height={height} viewBox={`0 0 16 ${height}`} aria-hidden="true">
      <path d={`M8 ${stops[0]} V${stops.at(-1)}`} className="reading-trace-track" />
      {!reduced && <><defs><motion.linearGradient id={gradient} gradientUnits="userSpaceOnUse" x1="0" x2="0" y1={tail} y2={beam}><stop stopColor="var(--bf-accent)" stopOpacity="0" /><stop offset="1" stopColor="var(--bf-accent)" /></motion.linearGradient></defs>
        <motion.line x1="8" x2="8" y1={tail} y2={beam} stroke={`url(#${gradient})`} strokeWidth="2" /></>}
      {stops.map((y, index) => <circle key={index} cx="8" cy={y} r="2.5" className="reading-trace-stop" />)}
      <motion.circle cx="8" cy={beam} r="4" className="reading-trace-head" />
    </svg>}
    {children}
  </div>;
}
